/**
 * Competitions page
 *
 * @module pages/CompetitionsPage
 */
import _ from 'lodash';
import BasePage from './BasePage';
import BodyStateAdapter from '../components/BodyStateAdapter';
import EventViewAdapter from '../views/EventViewAdapter';
import MarketMenuAdapter from '../components/MarketMenuAdapter';
import MessageBusTopics from '../../common/MessageBusTopics';
import SelectionViewAdapter from '../views/SelectionViewAdapter';
import URLProvider from '../components/URLProvider';
import LivescoreDiffusionAdapter from '../components/push/LivescoreDiffusionAdapter';
import SportsDataRepository from '../components/SportsDataRepository';

/**
 * Internal state flag used to determine whether page has been rendered or not
 *
 * @type {Boolean}
 */
var rendered = false;

/**
 * Page content in the form of a jQuery element
 *
 * @type {Object}
 */
var content = null;

/**
 * Page dependencies
 *
 * @type {Object}
 */
var deps = null;

/**
 * Page arguments
 *
 * @type {Object}
 */
var args = null;

/**
 * A cache to save the content of a competition with market group, content that be displayed multiple times
 * on a page, under a different filter, our market collection.
 *
 * @type {Object}
 */
var competitionMarketCache = {};

/**
 * Function used to bind event handles to various buttons/links on the page
 *
 * @param {Object} content jQuery object containing the page content
 * @param {Object} deps Page dependencies
 * @param {Object} args Page arguments
 *
 * @returns {void}
 *
 * @private
 */
var bindNavigation = function (content, deps, args) {

    initInternals(content, deps, args);
    bindTabs(content);
    bindAccordion();
};

/**
 * Function used to initialize interface
 *
 * @returns {void}
 *
 * @private
 */
var initInterface = function () {

    var model = deps.sportsDataRepository.getModel();

    SelectionViewAdapter.init(
        model.selections,
        content
    );

    _.each(
        model.events,
        subscribeToLivescore
    );

    if (isResponseEmpty(BasePage.jQuery('#competitions-tab-content').html())) {
        disableTab(args.selectedTab);
        redirectToNextTab();
    }
};

/**
 * Function used to reset the model
 *
 * @returns {void}
 *
 * @private
 */
var resetModel = function () {

    deps.pageCommon.DomPushAdapter.unsubscribeAll();
    deps.pageCommon.PushAdapter.unsubscribeAll();
    LivescoreDiffusionAdapter.unsubscribeAll();
    BasePage.ModelComposer.regenerateMergedModel(window.WH.sportsbook.model);
};

/**
 * Function used to initialize both model and interface
 *
 * @returns {void}
 *
 * @private
 */
var performInit = function () {

    resetModel();

    initInterface();

    updateSubscription(window.WH.sportsbook.model);

    scrollToExpandedCompetition();
};

/**
 * Scrolls to an expanded competition, if there is one present, and if it isn't already in view
 *
 * @returns {void}
 *
 * @private
 */
var scrollToExpandedCompetition = function scrollToExpandedCompetition() {

    var $expandedElement = BasePage.jQuery('li.-expanded[data-snippet="true"]:visible .event-container');

    if (args.competitionName && $expandedElement.length) {

        scrollToHeader($expandedElement);
    }
};

/**
 * Subscribed to given events and their descendants
 *
 * @param {Object} model
 *
 * @returns {void}
 *
 * @private
 */
var updateSubscription = function (model) {

    _.each(
        model,
        subscribeSetToPush
    );

    _.each(
        model.events,
        subscribeToLivescore
    );

    content
        .find('section.event header time, span.btmarket__name time.eventStartTime')
        .each(renderDate);
};

/**
 * Binds Competition tabs
 *
 * @returns {void}
 *
 * @private
 */
var bindTabs = function (content) {

    var bindings = [{
        selector: '#comp-navigation-tabs a.tabs__nav-item',
        clickGroup: 'competitionsTabs',
        bindFunctions: [
            competitionTabAction,
        ],
    }];

    _.each(bindings, applyBindings.bind(this, content));
};


/**
 * Binds Accordion tabs
 *
 * @returns {void}
 *
 * @private
 */
var bindAccordion = function () {

    var boundApplyFn = applyBindings.bind(this, content);

    var commonBindings = [
        getEventContainerToggleBinding(args.selectedTab),
        bindShowMoreCompetitions(),
    ];

    var tabSpecificBindings = {
        'outrights': [
            {
                selector: '#competitions-tab-content .header-dropdown--level2',
                clickGroup: 'outrightsCompetition',
                bindFunctions: [
                    outrightsCompetitionAction,
                ],
            },
        ],
    };

    bindShowMore(0, '#competitions-tab-content');
    _.each(commonBindings, boundApplyFn);
    _.each(tabSpecificBindings[args.selectedTab], boundApplyFn);
};

/**
 * Binds events show more button
 *
 * @param {Number} page number
 * @param {String} snippetId The snippet Id
 *
 * @private
 */
var bindShowMore = function bindShowMore(page, snippetId) {

    if (page === 0) {

        var showMoreBinding = {
            selector: snippetId + ' .-head5:not(.show-more-events)',
            clickGroup: 'showMoreEvents',
            bindFunctions: [
                showMoreEventsAction,
            ],
        };

        applyBindings(content, showMoreBinding);
    }
};

/**
 * Binds competitions show more button
 *
 * @returns {Object} A binding for show more in competitions
 *
 * @private
 */
var bindShowMoreCompetitions = function bindShowMoreCompetitions() {

    /**
     * Hiding show more button
     *
     * @returns {void}
     *
     * @private
     */
    var toggleAndScroll = function hide() {

        var $element = BasePage.jQuery(this);

        if ($element.data('expanded') === 'no') {

            scrollToHeader($element);
        }
    };

    return {
        selector: '#competitions-tab-content .-head5:not(.show-more-events):not(.initially-open)',
        clickGroup: 'showMoreCompetitions',
        bindFunctions: [toggleAndScroll],
    };
};

/**
 * Binds selections show more button
 *
 * @param {String} snippetId A snippet id attribute value
 *
 * @returns {void}
 *
 * @private
 */
var bindShowMoreSelections = function bindShowMoreSelections(snippetId) {

    var fragmentClass = 'outrights-fragment',
        shownClass = 'outrights-fragment-shown';

    /**
     * Hiding show more button and change class
     *
     * @returns {void}
     *
     * @private
     */
    var changeClassAndHide = function changeClassAndHide() {

        BasePage.jQuery(this)
            .closest('.' + fragmentClass)
            .removeClass(fragmentClass)
            .addClass(shownClass);
    };

    BasePage.jQuery(snippetId)
        .closest('.' + shownClass)
        .removeClass(shownClass)
        .addClass(fragmentClass);

    applyBindings(content, {
        selector: '#competitions-tab-content .-head5.show-more-events ,' +
        ' #competitions-tab-content',
        clickGroup: 'showMoreSelections',
        bindFunctions: [changeClassAndHide],
    });
};

/**
 * Action handling click on a tab item
 *
 * @param {Event} event Click event if triggered by user
 * @param {Boolean} redirect If true reloading snippet will replace history instead of appending to it
 *
 * @returns {boolean}
 *
 * @private
 */
var competitionTabAction = function (event, redirect) {

    var tabItem = BasePage.jQuery(this);
    args.selectedTab = tabItem.data('link-panel');
    reloadCompetitionTab(tabItem, !!redirect);

    handleTabClass(tabItem);

    return false;
};

/**
 * Action handling click on expanding a competition
 *
 * @returns {Boolean}
 *
 * @private
 */
var competitionEventsAction = function competitionEventsAction() {

    toggleEventsContainer(BasePage.jQuery(this), '-expanded', 0, 'competitionEvents');
};

/**
 * Action handling click on expanding a competition in outrights tab
 *
 * @returns {Boolean}
 *
 * @private
 */
var outrightsCompetitionAction = function outrightsCompetitionAction() {

    toggleOutrightCompetition(BasePage.jQuery(this),
        '-expanded', 0, 'competitionOutrights');
};

/**
 * Action handling click on show more events from competition
 *
 * @returns {Boolean}
 *
 * @private
 */
var showMoreEventsAction = function showMoreEventsAction() {

    var showMoreButton = BasePage.jQuery(this);
    toggleText(showMoreButton);
    toggleEventsContainer(showMoreButton, '-expanded', 1, 'competitionEvents');
};

/**
 * Toggles the show more/less text on a button
 *
 * @param {object} $element jQuery representation of the button we want to change the text on
 *
 * @returns {void}
 *
 * @private
 */
var toggleText = function toggleText($element) {

    if ($element.data('expanded') === 'no') {

        $element.text($element.data('show-less-text'));
        $element.data('expanded', 'yes');
    } else {

        $element.text($element.data('show-more-text'));
        $element.data('expanded', 'no');
    }
};

/**
 * Gets the offset.top value of the header, 0 if the offset object is undefined
 *
 * @param {Object} $button jQuery node
 *
 * @return {number} offset top value
 */
var getTargetHeaderPos = function getTargetHeaderPos($button) {

    var offset = $button.closest('li').prev().offset();
    return offset ? offset.top : 0;
};

/**
 * Scroll window view to fit provided market header in view
 *
 * @param {Object} $button jQuery node
 *
 * @returns {void}
 *
 * @private
 */
var scrollToHeader = function scrollToHeader($button) {

    var $window = BasePage.jQuery(window);
    var targetHeaderPos = getTargetHeaderPos($button);
    var topBarOffset = BasePage.jQuery('#header').height();
    if (headerNotInView($window, targetHeaderPos, topBarOffset)) {

        $window.scrollTop(targetHeaderPos - topBarOffset);
    }
};

/**
 * Determines whether the target element for a scrollTo event is in view
 *
 * @param {Object} $window jQuery representation of the window
 * @param {Number} targetHeaderPos The y position of the target element
 * @param {Number} topBarOffset The size of the top bar (we don't want this covering the element)
 *
 * @returns {boolean} Whether the window needs to be scrolled to reveal the element
 *
 * @private
 */
var headerNotInView = function headerNotInView($window, targetHeaderPos, topBarOffset) {

    return $window.scrollTop() + topBarOffset > targetHeaderPos ||
        $window.scrollTop() + ($window.height() / 2) < targetHeaderPos;
};

/**
 * Binds functions to the given binding Object
 *
 * @param {Object} content jQuery element
 * @param {Object} binding Object with required attributes:
 *     @param {String} selector jQuery selector
 *     @param {String} clickGroup Event Namespace
 *     @param {Array} bindFunctions An Array of functions to bind to a click event
 *
 * @returns {void}
 *
 * @private
 */
var applyBindings = function (content, binding) {

    var selector = binding.selector;
    var clickGroup = binding.clickGroup;

    /**
     * Binds a function to the given selector on a click event
     *
     * @param {Function} bindFunction The function to band
     *
     * @returns {void}
     *
     * @private
     */
    var applyBindFunction = function (bindFunction) {

        content.find(selector).on('click.' + clickGroup, bindFunction);
    };

    if (content instanceof BasePage.jQuery) {
        content.find(selector).off('click.' + clickGroup);
        _.each(binding.bindFunctions, applyBindFunction);
    }
};

/**
 * Flow of no-data redirects
 *
 * @private
 */
var noDataRedirectsFlow = {
    'matches': 'outrights',
    'outrights': 'matches',
};

/**
 * Is response empty string - no data
 *
 * @param {String} data Fragment response
 *
 * @returns {Boolean}
 *
 * @private
 */
var isResponseEmpty = function isResponseEmpty(data) {

    return data === '';
};

/**
 * Switch to next tab based on noDataRedirectsFlow.
 *
 * @returns {void}
 *
 * @private
 */
var redirectToNextTab = function redirectToNextTab() {

    var nextTab = noDataRedirectsFlow[args.selectedTab];
    var nextTabElem = BasePage.jQuery('.tabs__nav-item[data-link-panel="' + nextTab + '"]');

    if (nextTab && !nextTabElem.hasClass('tabs__nav-item--disabled')) {

        redirectToTab(nextTab);
    }
};

/**
 * Redirect to tab by it's name
 *
 * @param {String} tabName Unique tab name
 *
 * @returns {void}
 *
 * @private
 */
var redirectToTab = function redirectToTab(tabName) {

    var el = BasePage.jQuery('.tabs__nav-item[data-link-panel="' + tabName + '"]');
    competitionTabAction.apply(el, [null, true]);
};

/**
 * Add disabled class by tab name
 *
 * @param {String} tabName Tab name
 *
 * @returns {void}
 *
 * @private
 */
var disableTab = function disableTab(tabName) {

    BasePage.jQuery('.tabs__nav-item[data-link-panel="' + tabName + '"]')
        .addClass('tabs__nav-item--disabled');
};

/**
 * Function to reload page fragment
 *
 * @param {Object} button jQuery button
 * @param {Boolean} redirect If true reloading snippet will replace history instead of appending to it
 *
 * @returns {void}
 *
 * @private
 */
var reloadCompetitionTab = function reloadCompetitionTab(button, redirect) {

    var lastData;

    var URLSet = getTabsURLSet();

    URLSet.replaceHistory = !!redirect;

    if (!button.hasClass('-active')) {

        deps.pageCommon.reloadSnippet(URLSet, '#competitions-tab-content', {

            /**
             * Handle success response. Continue rendering tab or redirect to next one.
             *
             * @returns {void}
             */
            'afterSuccess': function afterSuccess() {

                if (isResponseEmpty(lastData)) {

                    disableTab(args.selectedTab);
                    redirectToNextTab();
                } else {

                    changeTabSuccess();
                }

            },

            /**
             * Grab response content to analyse in success callback
             *
             * @param {String} data Fragment response
             *
             * @returns {String}
             */
            'preRender': function preRender(data) {

                lastData = data;
                return data;
            },
        });
    } else {

        //Don't reload snippet, just push new url state
        deps.pageCommon.navigate(URLSet.redirectURL, false);
    }
};

/**
 * The same event container, representing a competition
 * (with a content determined by a market group / headline summary) can be displayed multiple times on the
 * page, under different regions. In order to uniquely determine what containers we want to toggle, on a
 * given action we can add the parent region (filter) to specific attributes of some container elements.
 *
 * @param {String} urlKey Helping us to differentiate between loading outright events fragment and regular
 * @param {String} regionSlug The slug of the region we want to add as an attribute on toggles
 * @param {Object} content Event container that we placed inside a region
 *
 * @returns {void}
 *
 * @private
 */
var addRegionDataAttributes = function addRegionDataAttributes(urlKey, regionSlug, content) {

    if (urlKey === 'competitionOutrights') {

        //an outright events container has multiple market groups that can be toggled
        var marketGroups = content.children();
        _.each(marketGroups,
            addRegionAttrToOutrightMGContainer.bind(this, regionSlug));

        //and the first group is expanded so have to update the show more
        addRegionAttrToShowMore(regionSlug,
            content.find('ul.-opened li.-expanded').children());
    } else {

        //just update show more
        addRegionAttrToShowMore(regionSlug, content);
    }
};

/**
 * Updating a toggle button with the current region.
 *
 * @param {String} regionSlug The slug of the region we want to add as an attribute on toggles
 * @param {Object} toggleButton Event container that we placed inside a region
 * @param {Boolean} more for show more
 *
 * @returns {String} the snippet key, build with region, competition and market group to be unique on page
 *
 * @private
 */
var addRegionAttrToToggleButton = function addRegionAttrToToggleButton(regionSlug, toggleButton, more) {

    var marketKey = getDataFromTag(toggleButton, 'competition-market-key');
    var snippetKey = regionSlug + '-' + marketKey;
    toggleButton.attr('data-id', snippetKey);
    toggleButton.attr('data-region-id', regionSlug);
    toggleButton.attr('data-toggle-send', snippetKey + (more ? '-more' : ''));
    return snippetKey;
};

/**
 * Gets a data param from a jquery element or it's children
 *
 * @param {Object} element A jQuery element representing the dom node to query
 * @param {String} dataAttribute The data attribute to query in the dom
 *
 * @returns {String} The value of the data tag
 *
 * @private
 */
var getDataFromTag = function getDataFromTag(element, dataAttribute) {

    return element.data(dataAttribute) || element.find('[data-' + dataAttribute + ']').data(dataAttribute);
};

/**
 * An event container can have a show more button that should toggle the fragment below. We need to add the
 * region parameter so toggle won't toggle a fragment from the same competition but other region.
 *
 * @param {String} regionSlug The slug of the region we want to add as an attribute on toggles
 * @param {Object} eventContainer Event container that we placed inside a region
 *
 * @returns {void}
 *
 * @private
 */
var addRegionAttrToShowMore = function addRegionAttrToShowMore(regionSlug, eventContainer) {

    var eventsToggle = eventContainer.find('.grid > a:not(.selection-show-more)');

    if (eventsToggle.length) {

        var snippetKey = addRegionAttrToToggleButton(regionSlug, eventsToggle, true);

        //update the fragment below the show more button
        var eventsGroup = BasePage.jQuery(eventContainer.find('[data-show-more]'));
        eventsGroup.attr('data-toggle-receive', snippetKey + '-more');
        eventsGroup.attr('id', 'comp-fragment-' + snippetKey + '-more');
    }
};

/**
 * An outright events container has multiple market groups that can be toggled. We need to add the
 * region parameter so toggle won't toggle a fragment from the same competition but other region.
 *
 * @param {String} regionSlug The slug of the region we want to add as an attribute on toggles
 * @param {Object} marketGroup A market group container placed inside a competition
 *
 * @returns {void}
 *
 * @private
 */
var addRegionAttrToOutrightMGContainer = function addRegionAttrToOutrightMGContainer(regionSlug, marketGroup) {

    marketGroup = BasePage.jQuery(marketGroup);
    var eventsToggle = marketGroup.find('.header-dropdown--level3'),
        eventsGroup = marketGroup.children().last();

    var snippetKey = addRegionAttrToToggleButton(regionSlug, eventsToggle);

    eventsGroup.attr('data-toggle-receive', snippetKey);
    eventsGroup.attr('id', 'comp-fragment-' + snippetKey);
    eventsGroup.attr('data-region-id', regionSlug);
};

/**
 * Function to reload page fragment
 *
 * @param {Object} button jQuery button
 * @param {String} expandedClass A class to indicate if we are expanding on collapsing
 * @param {Number} page number
 * @param {String} urlKey that indicates what url is returning the data we need for this container
 *
 * @returns {void}
 *
 * @private
 */
var toggleEventsContainer = function toggleEventsContainer(button, expandedClass, page, urlKey) {

    if (button.hasClass(expandedClass)) {

        collapseEventContainer(button);
        scrollToHeader(button);
    } else {

        expandEventsContainer(button, page, urlKey);
    }
};

/**
 * Function to toggle an outright competition
 *
 * @param {Object} button jQuery button
 * @param {String} expandedClass A class to indicate if we are expanding on collapsing
 * @param {Number} page number
 * @param {String} urlKey that indicates what url is returning the data we need for this container
 *
 * @returns {void}
 *
 * @private
 */
var toggleOutrightCompetition = function toggleOutrightCompetition(button, expandedClass, page, urlKey) {

    if (!button.hasClass(expandedClass)) {

        expandEventsContainer(button, page, urlKey);
    }
};

/**
 * Fetching events from server only if the respective container was empty, otherwise we just let it toggle..
 *
 * @param {Object} button jQuery button
 * @param {Number} page number
 * @param {string} urlKey that indicates what url is returning the data we need for this container
 *
 * @returns {void}
 *
 * @private
 */
var expandEventsContainer = function expandEventsContainer(button, page, urlKey) {

    var currentSnippetId = getCompetitionFragmentId(button, page);

    if (BasePage.jQuery(currentSnippetId).is(':empty')) {

        populateEventsContainer(button, page, urlKey, currentSnippetId);
    }

    updateNavigationRoute(button);

};

/**
 * Updates the routing, including navigation bar in the browser for the given element
 *
 * @param {Object} button jQuery element
 * @returns {void}
 *
 * @private
 */
var updateNavigationRoute = function updateNavigationRoute(button) {

    var aChild = button.children('a');

    if (aChild.length) {

        window.history.replaceState({}, aChild.text(), aChild.attr('href'));
    }
};

/**
 * Populate events container
 *
 * @param {Object} button jQuery button
 * @param {Number} page number
 * @param {String} urlKey that indicates what url is returning the data we need for this container
 * @param {String} currentSnippetId The current snippet id
 *
 * @returns {void}
 *
 * @private
 */
var populateEventsContainer = function populateEventsContainer(button, page, urlKey, currentSnippetId) {

    var marketKey = getDataFromTag(button, 'competition-market-key');

    var snippetsSelector = getEventContainerSelector(
        marketKey,
        page
    );

    //searching for the same content determined by the competition and market in the cache, if we don't
    //find it, we will add the fetched content in the success handler
    var existingContainer = competitionMarketCache[snippetsSelector];

    if (!existingContainer) {

        fetchEventContainer(button, page, urlKey, currentSnippetId, snippetsSelector);
    } else {

        copyExistingEventContainer(button, page, urlKey,
            currentSnippetId, existingContainer);
    }
};

/**
 * Fetch new event container from server
 *
 * @param {Object} button jQuery button
 * @param {Number} page number
 * @param {String} urlKey that indicates what url is returning the data we need for this container
 * @param {String} currentSnippetId The current snippet id
 * @param {String} snippetsSelector The identical content snippets selector
 *
 * @returns {void}
 *
 * @private
 */
var fetchEventContainer = function fetchEventContainer(button, page, urlKey,
    currentSnippetId, snippetsSelector) {

    var competitionId = getDataFromTag(button, 'competition-id');

    var params = getLoadEventParams(button, page, competitionId);

    deps.pageCommon.reloadSnippet({
        requestURL: URLProvider.competitions[urlKey](params),
    }, currentSnippetId, {
        'afterSuccess':
            loadEventsSuccess.bind(this, page, urlKey,
                currentSnippetId, snippetsSelector),
        'afterError': handleSnippetLoadError.bind(this, currentSnippetId),
    });
};

/**
 * Fetch new event container from server
 *
 * @param {Object} button jQuery button
 * @param {Number} page number
 * @param {String} urlKey that indicates what url is returning the data we need for this container
 * @param {String} currentSnippetId The current snippet id
 * @param {Object} existingContainer The already existing content for this market group and competition
 *
 * @returns {void}
 *
 * @private
 */
var copyExistingEventContainer = function copyExistingEventContainer(button, page, urlKey,
    currentSnippetId, existingContainer) {

    var snippetId = getCompetitionFragmentId(button, page);
    var newFragment = BasePage.jQuery(snippetId);
    newFragment.html(existingContainer.html());

    processLoadedEvents(page, urlKey, currentSnippetId, newFragment);
    resetContainerState(newFragment, urlKey, page);
    SelectionViewAdapter.init();
};

/**
 * Reset container state after a copy.
 *
 * @param {Object} container jQuery container
 * @param {String} urlKey that indicates what url is returning the data we need for this container
 * @param {Number} page number
 *
 * @returns {void}
 *
 * @private
 */
var resetContainerState = function resetContainerState(container, urlKey, page) {

    if (urlKey === 'competitionOutrights') {

        //make sure the first market group is expanded and the rest are collapsed
        var marketGroups = container.find('.competition-outright-markets >ul');
        _.each(_.rest(marketGroups),
            hideMarketGroup);
    } else {

        resetShowMoreState(container, page);
    }
};

/**
 * Reset container state after a copy.
 *
 * @param {Object} container jQuery container
 * @param {Number} page number
 *
 * @returns {void}
 *
 * @private
 */
var resetShowMoreState = function resetShowMoreState(container, page) {

    if (page === 0) {

        var toggleButton = container.find('a.grid-col');
        toggleButton.show().removeClass('-expanded');
        toggleButton.parent().removeClass('-opened');
        container.find('[data-show-more]').empty().removeClass('-expanded');
    }
};

/**
 * Hide a market group from outright
 *
 * @param {Object} marketGroup A market group container from outrights
 *
 * @returns {void}
 *
 * @private
 */
var hideMarketGroup = function hideMarketGroup(marketGroup) {

    marketGroup = BasePage.jQuery(marketGroup);
    setCollapseClass(marketGroup);
    var eventContainer = marketGroup.children().eq(1);
    eventContainer.removeClass('-expanded');
    eventContainer.hide();
};

/**
 * set collapse class
 *
 * @param {Object} marketGroup A market group container from outrights
 *
 * @returns {void}
 *
 * @private
 */
var setCollapseClass = function setCollapseClass(marketGroup) {

    marketGroup.removeClass('-opened');
    var header = marketGroup.find('aside.header__toolbar');
    header.removeClass('-opened');
    header.find('.button-clear').removeClass('-expanded');
};

/**
 * Getting the parameters to use when requesting new events from the server.
 *
 * @param {Object} button jQuery button
 * @param {Number} page number
 * @param {String} competitionId The id of the competition
 *
 * @returns {Object} ulr params to load events
 *
 * @private
 */
var getLoadEventParams = function getLoadEventParams(button, page, competitionId) {

    var params = _.extend({}, args, {
        competitionId: competitionId,
        pagination: page,
    });

    if (args.selectedTab !== 'matches') {

        params.marketGroupName = button.data('market-group-name');
    }

    return params;
};

/**
 * On load error, we notify the pre loader manager and show the no betting message
 *
 * @param {String} snippetId The of the snippet that failed to load
 *
 * @returns {void}
 *
 * @private
 */
var handleSnippetLoadError = function handleSnippetLoadError(snippetId) {

    BasePage.jQuery(snippetId).append(getNoBettingMessage());
};

/**
 * Given a tab it indicates what are the elements that have to be bound to the generic load events action.
 * In the first tab (matches) we have it on level 2, in outright on level 3, in collections on level 4
 *
 * @param {String} currentTab The current tab
 * @param {String} parentSelector An optional parent sector, otherwise we will search the whole tab content
 *
 * @returns {Object} a binding for the generic load events action
 *
 * @private
 */
var getEventContainerToggleBinding = function getEventContainerToggleBinding(currentTab, parentSelector) {

    parentSelector = parentSelector || '#competitions-tab-content';

    var toggleSelector = parentSelector + ' .header-dropdown--level' +
        (['matches', 'outrights'].indexOf(currentTab) + 2);

    return {
        selector: toggleSelector,
        clickGroup: 'competitionEvents',
        bindFunctions: [
            competitionEventsAction,
        ],
    };
};

/**
 * Function to collapse an event fragment
 *
 * @param {Object} button jQuery button
 *
 * @returns {void}
 *
 * @private
 */
var collapseEventContainer = function collapseEventContainer(button) {

    var competitionMarketKey = button.attr('data-competition-market-key'),
        snippetsSelector = getEventContainerSelector(competitionMarketKey, 0),
        $snippetsSelector = BasePage.jQuery(snippetsSelector),
        visibleSnippets = $snippetsSelector.filter(':visible');

    //it was the last visible container with this content
    if (visibleSnippets.length === 1) {

        cleanupRemovedEvents(visibleSnippets.eq(0));
        competitionMarketCache =
            _.omit(competitionMarketCache, snippetsSelector);

        $snippetsSelector.empty();
    }
};

/**
 * After removing events from Dom, we have to exclude them from the model, push and livescore.
 *
 * @param {Object} collapsedEventContainer The collapsed event container
 *
 * @returns {void}
 *
 * @private
 */
var cleanupRemovedEvents = function cleanupRemovedEvents(collapsedEventContainer) {

    //Find the markets from the container, cause they are unique on the page, unlike events.
    //Remove from the model the markets from events and then remove the events with no markets left
    var removedMarketIds = _.chain(collapsedEventContainer.find('.event__side'))
        .map(extractMarkets)
        .flatten()
        .map(extractEntityId)
        .value();

    var marketableEvents = collapsedEventContainer.find('.event');
    var removedEventsIds =
        _.map(marketableEvents, extractEntityId);

    processRemovedEntities(removedMarketIds, removedEventsIds);
};

/**
 * Processing the removed dom elements in order to clean up the model as well.
 *
 * @param {Array} removedMarketIds The collection of market ids that was removed from the dom by collapsing
 * @param {Array} removedEventsIds The collection of event ids that was removed from the dom by collapsing
 *
 * @returns {void}
 *
 * @private
 */
var processRemovedEntities = function processRemovedEntities(removedMarketIds, removedEventsIds) {

    /**
     * Processing a removed from dom event element
     *
     * @param {String} removedEventId The id of a removed event
     *
     * @returns {void}
     *
     * @private
     */
    var processRemovedEvent = function processRemovedEvent(removedEventId) {

        var eventModel = window.WH.sportsbook.model.events[removedEventId];

        if (eventModel) {

            //remove from the event model markets the ones that were removed from the dom by collapsing
            eventModel.markets = _.difference(eventModel.markets, removedMarketIds);

            removeEventFromModel(eventModel);
        }

    };

    _.each(removedEventsIds, processRemovedEvent);
    BasePage.ModelComposer.removeMarketsFromModel(removedMarketIds);
};

/**
 * Try to remove the event as well if there are no markets left (was displayed just once)
 *
 * @param {Object} eventModel the event model
 *
 * @returns {void}
 *
 * @private
 */
var removeEventFromModel = function removeEventFromModel(eventModel) {

    if (_.isEmpty(eventModel.markets)) {

        BasePage.ModelComposer.removeEventsFromModel([eventModel.id]);
        LivescoreDiffusionAdapter.unsubscribe(eventModel.id);
    }
};

/**
 * Extract the markets from a event market container
 *
 * @param {Object} marketsContainer dom container
 *
 * @returns {String} the event id
 *
 * @private
 */
var extractMarkets = function extractMarkets(marketsContainer) {

    return BasePage.jQuery(marketsContainer).children();
};

/**
 * Extract the id of the marketable event from a jQuery object
 *
 * @param {Object} entity jQuery An dom element representing an entity like; event, market, selection
 *
 * @returns {String} the event id
 *
 * @private
 */
var extractEntityId = function extractEntityId(entity) {

    return BasePage.jQuery(entity).attr('id');
};

/**
 * Update competition tab class
 *
 * @param {Object} tabItem jQuery button
 *
 * @returns {Boolean}
 *
 * @private
 */
var handleTabClass = function (tabItem) {

    var parents = tabItem.parents('#comp-navigation-tabs');

    parents.find('.tabs__nav-item')
        .removeClass('tabs__nav-item--active');

    parents.siblings('#competitions-tab-content').removeClass().addClass(tabItem.attr('data-link-panel'));

    tabItem.addClass('tabs__nav-item--active');
};

/**
 * Get the id of the fragment that has to be reloaded when triggering an expand on the given toggle
 *
 * @param {Object} button jQuery button
 * @param {Number} page the page number
 *
 * @returns {String}
 *
 * @private
 */
var getCompetitionFragmentId = function getCompetitionFragmentId(button, page) {

    return '#comp-fragment-' + button.data('id') + (page ? '-more' : '');
};

/**
 * The selector to use when fetching all the event containers regardless of the region where the comp is.
 *
 * @param {String} competitionMarketKey A string build with competition and market, unique per competition.
 * @param {Number} page the page number
 *
 * @returns {String} the selector to use we are doing batch updates of snippets under different regions
 *
 * @private
 */
var getEventContainerSelector = function getEventContainerSelector(competitionMarketKey, page) {

    return page ? 'div[data-competition-market-key="' + competitionMarketKey + '"][data-show-more]' :
        'li[data-competition-market-key="' + competitionMarketKey + '"]';
};

/**
 * Gets the url set for a tab
 *
 * @returns {Object}
 *
 * @private
 */
var getTabsURLSet = function () {

    var requestURL = URLProvider.competitions.tabFragment(args);
    var redirectURL = URLProvider.competitions.redirectByFilter(args);

    return {
        requestURL: requestURL,
        redirectURL: redirectURL,
    };
};

/**
 * Function to call when we changed the tab.
 *
 * @returns {void}
 *
 * @private
 */
var changeTabSuccess = function () {

    bindAccordion();
    resetModel();
    initInterface();

    competitionMarketCache = {};

    if (window.WH && window.WH.messageBus) {

        window.WH.messageBus.publish(MessageBusTopics.NAVIGATION_PAGE_CHANGED);
    }
};

/**
 * The event can be displayed multiple times on the page, so when loading a fragment that contains an
 * already existing event, but this time for another market, we should update the model of the new event
 * with was already present.
 *
 * @param {Object} modelBoxEvent A fetched model box event that will be added to the existing model
 *
 * @returns {void}
 *
 * @private
 */
var mergeModelMarkets = function mergeModelMarkets(modelBoxEvent) {

    var existingEventModel = window.WH.sportsbook.model.events[modelBoxEvent.id];
    if (existingEventModel && existingEventModel.markets) {

        modelBoxEvent.markets = existingEventModel.markets.concat(modelBoxEvent.markets);
    }
};

/**
 * Function to add to model and subscribe the newly inserted events after a fragment load
 *
 * @param {Object} modelBox An object with the following keys : 'events', 'selections', 'markets'
 *
 * @returns {void}
 *
 * @private
 */
var processNewModelBox = function processNewModelBox(modelBox) {

    //if an event is loaded multiple times, the previous event entry in the model will be replaced
    _.each(modelBox.events, mergeModelMarkets);
    BasePage.ModelComposer.extendModel(modelBox);

    updateSubscription(modelBox);

    SelectionViewAdapter.init(
        modelBox.selections,
        content
    );
};

/**
 * Function to call after we loaded events.
 *
 * @param {Number} page number
 * @param {String} urlKey that indicates what url is returning the data we need for this container
 * @param {String} snippetSelector The selector of the reloaded fragments
 * @param {String} snippetsSelector The identical content snippets selector
 *
 * @returns {void}
 *
 * @private
 */
var loadEventsSuccess = function loadEventsSuccess(page, urlKey, snippetSelector, snippetsSelector) {

    _.each(
        BasePage.ModelComposer.convertHashModelBox(window.WH.sportsbook.modelBox),
        processNewModelBox
    );

    //don't need the model boxes anymore, at this point the model is updated
    window.WH.sportsbook.modelBox = null;

    var eventContainer = BasePage.jQuery(snippetSelector);
    processLoadedEvents(page, urlKey, snippetSelector, eventContainer);

    //we loaded from the server new content -> we will add it in the cache with unique flag
    //This flag will help us when collapsing. If is true we don't need to search for other occurrences of
    //the same content. The flag is set to false when content is copied.
    competitionMarketCache[snippetsSelector] = eventContainer;

    bindShowMoreSelections(snippetSelector);
};

/**
 * Function to call after we loaded events, to process the dom
 *
 * @param {Number} page number
 * @param {String} urlKey that indicates what url is returning the data we need for this container
 * @param {String} snippetSelector The selector of the reloaded fragments
 *
 * @returns {void}
 *
 * @private
 */
var processLoadedEvents = function processLoadedEvents(page, urlKey, snippetSelector, eventContainer) {

    //The header is displayed for 'HH' and 'MR', so it can only be present for 'matches' and 'markets' tab
    if (page && args.selectedTab !== 'outrights') {

        removeDuplicateMarketGroupHeader(eventContainer);
    }

    var regionId = getRegionId(eventContainer);

    addRegionDataAttributes(urlKey, regionId,
        eventContainer.children());

    bindShowMore(page, snippetSelector);
    tabSpecificEventsSuccessHandler(urlKey, snippetSelector);
};

/**
 * Gets the region id data attribute from an element or its parent
 *
 * @param {Object} eventContainer jQuery element representing the event container to query
 *
 * @returns {String} The value of the region id attribute
 *
 * @private
 */
var getRegionId = function getRegionId(eventContainer) {

    return eventContainer.attr('data-region-id') ||
            eventContainer.parents().data('region-id') ||
            eventContainer.parents().parents().data('region-id');
};

/**
 * When loading a second page we might have to remove the market header if is the same as the last one from
 * the first page.
 *
 * @param {Object} eventContainer jQuery container
 *
 * @returns {void}
 *
 * @private
 */
var removeDuplicateMarketGroupHeader = function removeDuplicateMarketGroupHeader(eventContainer) {

    removeMatchesDuplicateHeader(eventContainer);
};

/**
 * On 'matches' tab we need to check the previous header from the same event container.
 *
 * @param {Object} eventContainer jQuery container
 *
 * @returns {void}
 *
 * @private
 */
var removeMatchesDuplicateHeader = function removeMatchesDuplicateHeader(fragmentContainer) {

    var eventContainer = fragmentContainer.closest('li'),
        lastHeader = eventContainer
            .find('div[data-show-more]')
            .prevAll(':parent')
            .first(),
        fragmentHeader = eventContainer
            .find('section.event-container:last')
            .find('div.market-group-wrapper')
            .first(':parent');

    if (lastHeader.data('marketgroupname') === fragmentHeader.data('marketgroupname')) {

        fragmentHeader.find('div.mg-header').remove();
        fragmentHeader.find('hr').first().remove();
    }
};

/**
 * Tab specific load events handler.
 *
 * @param {String} urlKey that indicates what url is returning the data we need for this container
 * @param {String} snippetId The id of the reloaded fragment
 *
 * @returns {void}
 *
 * @private
 */
var tabSpecificEventsSuccessHandler = function tabSpecificEventsSuccessHandler(urlKey, snippetId) {

    if (urlKey === 'competitionOutrights') {

        applyBindings(content,
            getEventContainerToggleBinding(args.selectedTab, snippetId));
    }
};

/**
 * Render the date as an element's text based on its datetime attribute
 *
 * @returns {void}
 *
 * @private
 */
var renderDate = function () {

    var startDate = BasePage.jQuery(this).attr('datetime');
    BasePage.jQuery(this).text(BasePage.Date.format(startDate, 'DD MMM HH:mm'));
};

/**
 * Subscribe an in play event to livescore updates
 *
 * @param {Object} event   Event model
 * @param {String} eventId Event ID
 *
 * @returns {void}
 *
 * @private
 */
var subscribeToLivescore = function (event, eventId) {

    if (event.isInPlay.toString() === 'true') {
        LivescoreDiffusionAdapter.subscribe(
            BasePage.getSportEnSlug(event),
            eventId,
            EventViewAdapter.livescore(eventId, content).update
        );
    }
};

/**
 * Subscribe a set of entities (events, markets, etc) to PUSH updates
 *
 * @param {Object} set An object containing entities such as markets, selections, events
 *
 * @returns {void}
 *
 * @private
 */
var subscribeSetToPush = function (set) {

    _.each(set, subscribeEntityToPush);
};

/**
 * Subscribe a single entity to PUSH updates
 *
 * @param {Object} entity Market/event/selection model
 *
 * @returns {void}
 *
 * @private
 */
var subscribeEntityToPush = function subscribeEntityToPush(entity) {

    //For markets we are preventing propagation, since an event can be displayed in multiple places
    if (entity && entity.id && !entity.id.match(/OB_TY/g) && deps && deps.pageCommon &&
        deps.pageCommon.DomPushAdapter.subscribe(entity, push.callback,
            entity.id.match(/OB_MA/g))) {

        deps.pageCommon.PushAdapter.subscribe(entity.pdsTopic);
    }
};

/**
 * If there is no filter provided in url, we check for selected tab in html
 *
 * @returns {String}
 *
 * @private
 */
var getInitialSelectedTab = function () {

    if (args.filter) {

        return args.filter;
    }

    var activeTabSelector = '#comp-navigation-tabs .tabs__nav-item.tabs__nav-item--active';

    return BasePage.jQuery(activeTabSelector).data('link-panel');
};

/**
 * Init internal data for later usage
 *
 * @param {Object} content jQuery container element
 * @param {Object} deps Page dependencies
 * @param {Object} args Page arguments
 *
 * @returns {void}
 *
 * @private
 */
var initInternals = function (pageContent, pageDeps, pageArgs) {

    content = pageContent;
    deps = pageDeps;
    args = pageArgs;

    args.selectedTab = getInitialSelectedTab();

    // init the sliding matches panel button
    MarketMenuAdapter.init();
};

/**
 * Returns a competitions page filter URL based on args
 *
 * @param {String} baseURL Base URL
 * @param {Object} args Page arguments
 *
 * @returns {String} Compiled URL
 *
 * @private
 */
var getCompetitionsPageURL = function (baseURL, args) {
    if (args.filter) {

        return baseURL + '/competitions/' + args.filter;
    }
    return baseURL + '/competitions';
};

/**
 * Returns a competitions page competition URL based on args
 *
 * @param {String} baseURL Base URL
 * @param {Object} args Page arguments
 *
 * @returns {String} Compiled URL
 *
 * @private
 */
var getCompetitionPageURL = function (baseURL, args) {
    var filter = args.filter || 'matches';

    var aUrl = [baseURL, args.page];

    if (args.competitionId) {
        aUrl.push(args.competitionId);
    }

    aUrl.push(args.competitionName);
    aUrl.push(filter);

    if (args.filterId && args.filterName) {
        aUrl.push(args.filterId);
        aUrl.push(args.filterName);
    }

    return aUrl.join('/');
};

/**
 * Removes an event element if necessary
 *
 * @param {String} marketId the Id of the market
 *
 * @returns {void}
 *
 * @private
 */
var removeEventFromMarket = function removeEventFromMarket(marketId) {

    var $market = BasePage.jQuery('#' + marketId);
    var $event = $market.closest('.event');
    if ($event.length && ($event.attr('data-betinrun') === 'false')) {

        var parentEventContainer = $event.closest('li[data-snippet]'),
            marketGroupContainer = $event.closest('.markets-group-container');

        $event.remove();
        processEventHide(parentEventContainer, marketGroupContainer);
    }
};

/**
 * Hide the market group header if needed
 *
 * @param {Object} marketGroupContainer we should hide the header from (ex: 1 x 2) when left empty
 *
 * @returns {void}
 *
 * @private
 */
var hideMarketGroupHeader = function showMarketGroup(marketGroupContainer) {

    if (marketGroupContainer.find('.event:visible').length === 0) {

        marketGroupContainer.find('.group-title').hide();
        marketGroupContainer.find('.subheader').hide();
    }
};

/**
 * Show the market group header if needed
 *
 * @param {Object} marketGroupContainer show the header from, when we got the fist event from the group back
 *
 * @private
 */
var showMarketGroupHeader = function showMarketGroupHeader(marketGroupContainer) {

    if (marketGroupContainer.find('.event:visible').length === 1) {

        marketGroupContainer.find('.group-title').show();
        marketGroupContainer.find('.subheader').show();
    }
};

/**
 * If an event is displayed false or removed then we might have to show 'no events' message and remove the
 * market header (name and 1 x 2)
 *
 * @param {Object} parentEventsFragment loaded from the server, should contain a 'no events' when left empty
 * @param {Object} marketGroupContainer we should hide the header (ex: 1 x 2) when left empty
 *
 * @private
 */
var processEventHide = function processEventHide(parentEventsFragment, marketGroupContainer) {

    if ((parentEventsFragment.find('.event:visible').length === 0) &&
        (parentEventsFragment.find('.grid-col.-head5:visible').length === 0) &&
        (parentEventsFragment.find('div[data-no-events]').length === 0)) {

        parentEventsFragment.append(getNoBettingMessage());
    }

    hideMarketGroupHeader(marketGroupContainer);
};

/**
 * 'no betting' message markup. Can be used when last event was removedvia push, or we have an error on load
 *
 * @returns {String} the message to show
 *
 * @private
 */
var getNoBettingMessage = function getNoBettingMessage() {

    return '<div data-no-events>Sorry, there is currently no betting available.</div>';
};

/**
 * If an event is displayed true then we might have to remove the 'no events' message and put back the
 * market header (name and 1 x 2)
 *
 * @param {Object} parentEventsFragment loaded from the server, should contain a 'no events' when left empty
 * @param {Object} marketGroupContainer we should hide the header (ex: 1 x 2) when left empty
 *
 * @private
 */
var processEventShow = function processEventShow(parentEventsFragment, marketGroupContainer) {

    if (parentEventsFragment.find('.event:visible').length === 1) {

        parentEventsFragment.find('div[data-no-events]').remove();
    }

    showMarketGroupHeader(marketGroupContainer);
};

/**
 * This function checks if the entity was the last one displayed in the parent container in order to show
 * or hide friendly 'no events' messages
 *
 * @param {Object} $event jQuery element
 * @param {Boolean} hide flag
 *
 * @returns {void}
 *
 * @private
 */
var manageEventContainers = function manageEventContainers($event, hide) {

    //the container with events loaded from the server, should contain a 'no events' message when left empty
    var parentEventContainer = $event.closest('li[data-snippet]'),
        //market group parent, we should hide the header (ex: 1 x 2) when left empty, can be more in a snippet
        marketGroupContainer = $event.closest('.markets-group-container');

    if (hide) {

        processEventHide(parentEventContainer, marketGroupContainer);
    } else {

        processEventShow(parentEventContainer, marketGroupContainer);
    }
};

/**
 * Handler for a display message at event level
 *
 * @param {Object} message PUSH message
 *
 * @returns {void}
 *
 * @private
 */
var eventDisplayedUpdateCase = function eventDisplayedUpdateCase(message) {

    /**
     * Since an event can appear more the once on this page, this function will process each one of them
     *
     * @param {Object} eventContainer The jquery event element
     *
     * @returns {void}
     *
     * @private
     */
    var processEventContainer = function processEventContainer(eventContainer) {

        manageEventContainers(BasePage.jQuery(eventContainer), !message.value);
    };

    if (message.nodeType === 'events') {

        var eventContainers = BasePage.jQuery('section[id=\'' + message.id + '\']');
        _.each(eventContainers, processEventContainer);
    }
};

/**
 * Handler for a display message at market level
 *
 * @param {Object} message PUSH message
 *
 * @returns {void}
 *
 * @private
 */
var marketDisplayedUpdateCase = function marketDisplayedUpdateCase(message) {

    if (message.nodeType === 'markets' && window.WH.sportsbook.model.markets[message.id]) {

        var market = window.WH.sportsbook.model.markets[message.id];
        marketDisplayedUpdatePropagation(message, market);
    }
};

/**
 * Propagates a visibility change from the market to the parent event if needed
 *
 * @param {Object} message PUSH message
 * @param {Object} market The market model
 *
 * @returns {void}
 *
 * @private
 */
var marketDisplayedUpdatePropagation = function marketDisplayedUpdatePropagation(message, market) {

    /**
     * Processing a market container, since the same market can be multiple times, under different regions
     *
     * @param {Object} marketContainer The marketContainer to process
     *
     * @returns {void}
     *
     * @private
     */
    var processMarketContainer = function processMarketContainer(marketContainer) {

        var $market = BasePage.jQuery(marketContainer);
        //if there are no visible market siblings of the updated market, we need toggle the event as well
        if ($market.siblings(':visible').length === 0) {

            processEventVisibility($market, market, message.value);
        }
    };

    var marketContainers = BasePage.jQuery('div[id=\'' + message.id + '\']');
    _.each(marketContainers, processMarketContainer);
};

/**
 * Processing the event visibility if a change from the markets propagated here
 *
 * @param {Object} $market jquery market object
 * @param {Object} market The market model
 * @param {Boolean} show A flag that indicates if the event should be made visible or hidden
 *
 * @returns {void}
 *
 * @private
 */
var processEventVisibility = function processEventVisibility($market, market, show) {

    if (market && window.WH.sportsbook.model.events[market.eventId]) {

        var event = window.WH.sportsbook.model.events[market.eventId];
        event.displayedOutModel = show;

        var $event = $market.closest('#' + market.eventId);
        toggleEventVisibility($event, show);
    }
};

/**
 * Toggle event visibility
 *
 * @param {Object} $event jquery market object
 * @param {Boolean} show A flag that indicates if the event should be made visible or hidden
 *
 * @returns {void}
 *
 * @private
 */
var toggleEventVisibility = function toggleEventVisibility($event, show) {

    if (!$event.hasClass('noHide')) {

        $event.toggleClass('displayNone', !show)
            .attr('data-displayed', show);
        manageEventContainers($event, !show);
    }
};

/**
 * Push related functionality
 *
 * @type {Object}
 */
var push = {

    /**
     * DOM PUSH update callback function. This function is called every time a push message comes in
     *
     * @param {Object} message DOM PUSH message
     *
     * @private
     */
    callback: function callback(message) {

        if (push.handlers.hasOwnProperty(message.attribute)) {

            push[push.handlers[message.attribute]](message);
        }
    },

    /**
     * PUSH handlers available per attribute type
     *
     * @type {Object}
     */
    handlers: {
        'betInRun': 'betInRunUpdateCase',
        'displayed': 'displayedUpdateCase',
    },

    /**
     * handling events and markets since for this page market -> event propagation is disabled cause we
     * can have the event more then once (under different market) at the same time.
     *
     * @param {Object} message PUSH message
     *
     * @return {void}
     *
     * @private
     */
    displayedUpdateCase: function displayedUpdateCase(message) {

        eventDisplayedUpdateCase(message);
        marketDisplayedUpdateCase(message);
    },

    /**
     * Handles the dom push message containing an betInRun attribute
     *
     * @param {Object} message DOM PUSH message
     *
     * @return {void}
     *
     * @private
     */
    betInRunUpdateCase: function betInRunUpdateCase(message) {

        if (message.value === true &&
            message.nodeType === 'events' &&
            window.WH.sportsbook.model.events[message.id]) {

            BasePage.propagateTopBetsBetInRun(message);

            var eventMarkets = window.WH.sportsbook.model.events[message.id].markets;

            //the event can be displayed multiple times on this page, under different markets
            _.each(eventMarkets, removeEventFromMarket);

            //model composer will remove the event with the markets and selections
            BasePage.ModelComposer.removeEventsFromModel([message.id]);
        }
    },
};

/**
 * Contains functions exposed by this component
 * as an API
 *
 * @type {Object}
 */
var api = {

    /**
     * Return the URL associated with this page
     * based on the args
     *
     * @param   {Object} args Page arguments
     *
     * @returns {String}      Compiled URL based on args
     *
     * @public
     */
    getContentURL: function (args) {

        var baseURL = '/betting/' + args.locale + '/' + args.sport;

        if (args.competitionName) {

            return getCompetitionPageURL(baseURL, args);
        }

        return getCompetitionsPageURL(baseURL, args);
    },

    /**
     * Function called after the DOM ready event is fired
     *
     * @returns {void}
     *
     * @public
     */
    postReady: function (deps, content, args) {

        BodyStateAdapter.init(BasePage.jQuery, 'competitions', args.sport);

        if (deps.ready) {

            deps.ready('competitions');
        }
    },

    /**
     * Triggered after render

     * @return {void}
     *
     * @public
     */
    postRender: function () {

        BasePage.switchTab('Competitions');
        BasePage.setOddsFormat();
        performInit();
    },

    bindNavigation: bindNavigation,

    /**
     * Changes rendered state flag to true
     *
     * @public
     */
    setRendered: function () {

        rendered = true;
    },

    /**
     * Changes rendered state flag to false
     *
     * @public
     */
    setUnrendered: function () {

        content = null;
        deps = null;
        args = null;
        competitionMarketCache = {};
        rendered = false;
    },

    /**
     * Returns the value of the internal rendered state flag
     *
     * @returns {Boolean} Returns true or false
     *
     * @public
     */
    isRendered: function () {

        return rendered;
    },

    /**
     * Slug to identify the current page
     */
    id: 'competitions',
};

export default api;
